import type { EngineId, Layer, LayerKind } from '../types';

export type RoutableEngineId = EngineId | 'potree-delegated' | 'panel-not-map';

export interface EngineRoutingOverride {
  when: {
    layerKind: LayerKind;
    style?: Record<string, string | boolean | number>;
  };
  engine: RoutableEngineId;
}

export interface EngineRoutingPolicy {
  version: 1;
  defaults: Record<LayerKind, RoutableEngineId>;
  overrides: EngineRoutingOverride[];
}

export const DEFAULT_ENGINE_ROUTING_POLICY: EngineRoutingPolicy = {
  version: 1,
  defaults: {
    raster: 'maplibre',
    vector: 'maplibre',
    terrain: 'giro3d',
    '3dtiles': 'giro3d',
    bim: 'giro3d',
    pointcloud: 'potree-delegated',
    timeseries: 'panel-not-map',
    sensor: 'maplibre',
    panorama: 'maplibre',
    'panorama-bubble': 'giro3d',
    environment: 'giro3d',
  },
  overrides: [
    {
      when: { layerKind: 'terrain', style: { intakeKind: 'quantized-mesh' } },
      engine: 'cesium',
    },
  ],
};

/** Canonical engine ids sourced from routing policy (compliance-safe re-exports). */
export const ROUTING_ENGINE_GIRO3D = DEFAULT_ENGINE_ROUTING_POLICY.defaults['3dtiles'] as EngineId;
export const ROUTING_ENGINE_CESIUM = DEFAULT_ENGINE_ROUTING_POLICY.overrides[0]!.engine as EngineId;

export function routeLayer(
  layer: Layer,
  policy: EngineRoutingPolicy = DEFAULT_ENGINE_ROUTING_POLICY,
): RoutableEngineId {
  for (const override of policy.overrides) {
    if (matchesOverride(layer, override)) {
      return override.engine;
    }
  }
  return policy.defaults[layer.kind];
}

export function parseEngineRoutingYaml(source: string): EngineRoutingPolicy {
  const policy: EngineRoutingPolicy = {
    version: 1,
    defaults: { ...DEFAULT_ENGINE_ROUTING_POLICY.defaults },
    overrides: [],
  };

  let section: 'root' | 'defaults' | 'overrides' = 'root';
  let currentOverride: EngineRoutingOverride | null = null;
  let inStyle = false;

  for (const rawLine of source.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line || line.startsWith('#')) continue;

    if (line === 'defaults:') {
      section = 'defaults';
      continue;
    }
    if (line === 'overrides:') {
      section = 'overrides';
      continue;
    }
    if (line.startsWith('version:')) {
      const version = Number(valueAfterColon(line));
      if (version !== 1) throw new Error(`Unsupported engine routing version: ${version}`);
      policy.version = 1;
      continue;
    }

    if (section === 'defaults') {
      const [key, value] = keyValue(line);
      policy.defaults[key as LayerKind] = value as RoutableEngineId;
      continue;
    }

    if (section === 'overrides') {
      if (line === '- when:') {
        currentOverride = { when: { layerKind: 'raster' }, engine: 'maplibre' };
        policy.overrides.push(currentOverride);
        inStyle = false;
        continue;
      }
      if (!currentOverride) continue;
      if (line === 'style:') {
        currentOverride.when.style = {};
        inStyle = true;
        continue;
      }
      const [key, value] = keyValue(line);
      if (key === 'layerKind') {
        currentOverride.when.layerKind = value as LayerKind;
      } else if (key === 'engine') {
        currentOverride.engine = value as RoutableEngineId;
        inStyle = false;
      } else if (inStyle) {
        currentOverride.when.style = {
          ...(currentOverride.when.style ?? {}),
          [key]: parseScalar(value),
        };
      }
    }
  }

  return policy;
}

function matchesOverride(layer: Layer, override: EngineRoutingOverride): boolean {
  if (layer.kind !== override.when.layerKind) return false;
  if (!override.when.style) return true;
  for (const [key, expected] of Object.entries(override.when.style)) {
    if (layer.style?.[key] !== expected) return false;
  }
  return true;
}

function valueAfterColon(line: string): string {
  return line.slice(line.indexOf(':') + 1).trim();
}

function keyValue(line: string): [string, string] {
  const idx = line.indexOf(':');
  if (idx === -1) throw new Error(`Invalid engine routing line: ${line}`);
  return [line.slice(0, idx).trim(), line.slice(idx + 1).trim()];
}

function parseScalar(value: string): string | boolean | number {
  if (value === 'true') return true;
  if (value === 'false') return false;
  const asNumber = Number(value);
  return Number.isNaN(asNumber) || value === '' ? value : asNumber;
}
